import { Directive, Injector, OnDestroy } from '@angular/core';
import { Coluna, DateFormatPipe, Exercicio, FormatoExportacao, FuncaoService, GlobalService, Login, LoginContabil, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LdoService } from '../../../../../planejamento/ldo/service/ldo.service';
import { Assinaturas } from '../../../assinaturas/assinaturas';
import { AnexoLRFService } from '../../service/anexo-lrf.service';
import { Alignment } from 'docx';
import { DatePipe } from '@angular/common';

@Directive()
export class DisponibilidadeFinanceiraRpps implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: LoginContabil = new Login();
  protected unsubscribe: Subject<void> = new Subject();

  public datepipe: DatePipe;

  constructor(
    protected anexoServico: AnexoLRFService,
    protected ldoServico: LdoService,
    protected mes: number,
    protected metaFiscal: number,
    protected exercicio: Exercicio,
    protected injector: Injector,
    protected assinaturaControleInterno: boolean) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // tslint:disable: max-line-length
  public async montarRelatorio(orgaos: number[], formato: FormatoExportacao = 'pdf') {

    this.anexoServico.disponibilidadeFinanceiraRpps(this.mes, this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async dados => {
        if (formato === 'pdf') {
          Relatorio.imprimirPersonalizado(
            `DEMONSTRATIVO DAS DISPONIBILIDADES FINANCEIRAS - RPPS`
            , this.login.usuario.nome, this.login.usuario.sobrenome,
            this.login.orgao.nome, this.login.brasao,
            await this.conteudo(dados),
            'portrait', 'DEMONSTRATIVO DAS DISPONIBILIDADES FINANCEIRAS - RPPS',
            {
              linhas: {
                hLineWidth() {
                  return 1;
                },
                vLineWidth() {
                  return 1;
                },
                hLineColor() {
                  return 'black';
                },
                paddingLeft() {
                  return 3;
                },
                paddingRight() {
                  return 3;
                }
              }
            });
        } else {
          this.funcaoService.exportar(formato, this.normalizar(dados), 'DEMONSTRATIVO DAS DISPONIBILIDADES FINANCEIRAS - RPPS', this.colunas());
        }
      });
  }

  private async conteudo(dados: any[]): Promise<{}[]> {
    // monta o cabecalho
    const registros: {}[] = [
      [
        {
          text: `REFERÊNCIA: ${this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano}`,
          alignment: 'center', bold: true, border: [false, false, false, false], colSpan: 4
        }, '', '', ''
      ],
    ];

    const ass = new Assinaturas(this.login.orgao, this.injector);
    let assinaturas = await ass.dadosAssinatura(null, false, null, this.assinaturaControleInterno, false, false, `${this.funcaoService.ultimoDiaMes(this.mes)}/${this.funcaoService.strZero(this.mes, 2)}/${this.exercicio.ano}`)

    // monta o agrupamento do relatório

    const receitas = dados.filter(item => item.tabela === 'Receitas');
    const despesas = dados.filter(item => item.tabela === 'Despesas');


    // Totalizadores
    const totalReceitas = receitas.reduce((acc, curr) => acc + Number(curr.valor), 0);
    const totalDespesas = despesas.reduce((acc, curr) => acc + Number(curr.valor), 0);


    const saldoAnterior = dados.filter(item => item.tabela === 'Saldo do Exercício Anterior');
    const saldoAtual = dados.filter(item => item.tabela === 'Saldo Atual');

    // Totalizadores
    const totalsaldoAnterior = saldoAnterior.reduce((acc, curr) => acc + Number(curr.valor), 0);
    const totalsaldoAtual = saldoAtual.reduce((acc, curr) => acc + Number(curr.valor), 0);

    for (let i = 0; i < Math.max(receitas.length, despesas.length); i++) {
      const receita = receitas[i];
      const despesa = despesas[i];
      if (i === 0) {
        registros.push([
          { text: 'Receitas', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalReceitas), bold: true, alignment: 'right', fontSize: 8, border: [false, true, false, false] },
          { text: 'Despesas', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalDespesas), alignment: 'right', bold: true, fontSize: 8, border: [false, true, false, false] },
        ]);
      }
      registros.push([
        { text: receita ? receita.titulo : '', bold: false, fontSize: 8, border: [false, false, false, false] },
        { text: this.funcaoService.convertToBrNumber(receita ? receita.valor : 0), alignment: 'right', fontSize: 8, border: [false, false, false, false] },
        { text: despesa ? despesa.titulo : '', bold: false, fontSize: 8, border: [false, false, false, false] },
        { text: this.funcaoService.convertToBrNumber(despesa ? despesa.valor : 0), alignment: 'right', fontSize: 8, border: [false, false, false, false] },
      ]);
    }

    for (let i = 0; i < Math.max(saldoAnterior.length, saldoAtual.length); i++) {
      const saldosAnterior = saldoAnterior[i];
      const saldosAtual = saldoAtual[i];
      if (i === 0) {
        registros.push([
          { text: 'Saldo do Exercício Anterior', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalsaldoAnterior), bold: true, alignment: 'right', fontSize: 8, border: [false, true, false, false] },
          { text: 'Saldo Atual', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalsaldoAtual), alignment: 'right', bold: true, fontSize: 8, border: [false, true, false, false] },
        ]);
      }
      registros.push([
        { text: saldosAnterior ? saldosAnterior.titulo : '', bold: false, fontSize: 8, border: [false, false, false, false] },
        { text: this.funcaoService.convertToBrNumber(saldosAnterior ? saldosAnterior.valor : 0), alignment: 'right', fontSize: 8, border: [false, false, false, false] },
        { text: saldosAtual ? saldosAtual.titulo : '', bold: false, fontSize: 8, border: [false, false, false, false] },
        { text: this.funcaoService.convertToBrNumber(saldosAtual ? saldosAtual.valor : 0), alignment: 'right', fontSize: 8, border: [false, false, false, false] },
      ]);
    }

    registros.push([
      { text: 'Total', bold: true, fontSize: 8, border: [false, true, false, false] },
      { text: this.funcaoService.convertToBrNumber(totalReceitas + totalsaldoAnterior), bold: true, alignment: 'right', fontSize: 8, border: [false, true, false, false] },
      { text: 'Total', bold: true, fontSize: 8, border: [false, true, false, false] },
      { text: this.funcaoService.convertToBrNumber(totalDespesas + totalsaldoAtual), alignment: 'right', bold: true, fontSize: 8, border: [false, true, false, false] },
    ]);

    registros.push([
      { text: 'Disponibilidade Financeira', bold: true, alignment: 'center', fontSize: 8, border: [false, true, false, true], margin: [0, 5, 0, 5], colSpan: 4 },
      { text: '', alignment: 'right', fontSize: 8, border: [false, true, false, true] },
      { text: '', bold: true, fontSize: 8, border: [false, true, false, true] },
      { text: '', alignment: 'right', bold: true, fontSize: 8, border: [false, true, false, true] },
    ]);

    const disponibilidades = dados.filter(item => item.tabela === 'Disposição Financeira');
    let valorRestos = 0;
    let valorRestoExercicio = 0;
    for (let i = 0; i < Math.max(disponibilidades.length); i++) {
      const disponibilidade = disponibilidades[i];
      if (i === 0) {
        registros.push([
          { text: 'Saldo Atual', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: '', alignment: 'right', fontSize: 8, border: [false, true, false, false] },
          { text: '', bold: true, fontSize: 8, border: [false, true, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalsaldoAtual), alignment: 'right', bold: true, fontSize: 8, border: [false, true, false, false] },
        ]);
        valorRestos = +disponibilidade.valor;
      }
      if (i === 1) {
        registros.push([
          { text: '( = ) Disponibilidade antes da Inscrição de Restos a Pagar do Exercício', bold: true, fontSize: 8, border: [false, false, false, false], colSpan: 3 },
          { text: '', border: [false, false, false, false] },
          { text: '', border: [false, false, false, false] },
          { text: this.funcaoService.convertToBrNumber(totalsaldoAtual - valorRestos), alignment: 'right', bold: true, fontSize: 8, border: [false, false, false, false] },
        ]);
        valorRestoExercicio = +disponibilidade.valor;
      }
      registros.push([
        { text: disponibilidade ? disponibilidade.titulo : '', bold: false, fontSize: 8, border: [false, false, false, false], colSpan: 3 },
        { text: '', border: false },
        { text: '', border: false },
        { text: this.funcaoService.convertToBrNumber(disponibilidade ? disponibilidade.valor : 0), alignment: 'right', fontSize: 8, border: [false, false, false, false] },
      ]);

    }

    registros.push([
      { text: '( = ) Disponibilidade Financeira', bold: true, fontSize: 8, border: [false, false, false, false], colSpan: 3 },
      { text: '', border: [false, false, false, false] },
      { text: '', border: [false, false, false, false] },
      { text: this.funcaoService.convertToBrNumber(totalsaldoAtual - (valorRestos + valorRestoExercicio)), alignment: 'right', bold: true, fontSize: 8, border: [false, false, false, false] },
    ]);

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 90, '*', 90],
        body: registros
      }
    }, {
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 0,
        widths: ['*', '*', '*'],
        body: assinaturas
      }
    }];
  }



  private montaDisponbilidade(registros: any) {
    registros.push([{ text: '', colSpan: 4, border: [true, false, true, false], margin: [0, 15, 0, 0] }]);
    registros.push(
      [
        { text: 'DISCRIMINAÇÃO DA META FISCAL', alignment: 'center', bold: true, fontSize: 8, colSpan: 3 },
        { text: '' },
        { text: '' },
        { text: 'VALOR', alignment: 'center', bold: true, fontSize: 7 }
      ], [
      { text: 'META DE RESULTADO NOMINAL FIXADA NO ANEXO DE METAS FISCAIS DA LDO P/ O EXERCÍCIO DE REFERÊNCIA', colSpan: 3 },
      { text: '' },
      { text: '' },
      { text: this.funcaoService.convertToBrNumber(0), alignment: 'center', bold: true, fontSize: 7 }
    ]
    );
  }

  public normalizar(lista: any[]) {
    const listaExportar = []

    for (const item of lista) {
    }
    return listaExportar
  }

  colunas(): Coluna[] {
    const colunasDefault: Coluna[] = [
      { titulo: 'ESPECIFICAÇÃO', coluna: 'especificacao', bold: true },
      { titulo: ``, coluna: 'a' },
      { titulo: ``, coluna: 'b' },
      { titulo: ``, coluna: 'c' },
    ];
    return colunasDefault;
  }
}
